import type { BaseObject as BaseObjectTypes, Nullable } from 'common/types'

import type { HORIZON, POLYGON, SEISMIC_SURVEY } from '../constants'
import type { SelectedTreeObject } from './workAreas'

type MLModalNavGroupType = 'settings' | 'initial'
export type MLModalNavItemType = 'algorithm' | 'calculations'
export type MLModalNavItem = {
  id: MLModalNavItemType
  groupId: MLModalNavGroupType
  label: string
  additionalText?: string
}

const AlgorithmTypesArr = [
  'horizonsExtension',
  'faultsSeparation',
  'seismophations',
  'stickSeparation',
] as const
export type AlgorithmTypes = (typeof AlgorithmTypesArr)[number]

export type AlgorithmTypeItems = { id: AlgorithmTypes; label: string }

const LinesTypeArr = ['inLine', 'xLine'] as const
type LinesType = (typeof LinesTypeArr)[number]

type CropSize = 0 | 92 | 128 | 512 | 1024

export const AggregationsArr = ['mean', 'max', 'weighted'] as const
export type AggregationType = (typeof AggregationsArr)[number]

const PostProcessingArr = ['no', 'filling', 'dispacking'] as const
type PostProcessingType = (typeof PostProcessingArr)[number]

export type CommonParams = {
  cropSize: CropSize
  iterations: number
  direction: LinesType
  overlap: number
  aggregation: AggregationType
  mlServiceToUse?: MLServiceToUseType
}

export type HorizonsExtensionParams = CommonParams & {
  postProcessing: PostProcessingType
}

export type FaultsSeparationParams = CommonParams & {
  stickSelection: boolean
  numberOfLeafs: number
}

export type SeismophationsParams = CommonParams & {
  additionalTraining: boolean
}

export type StickSeparationParams = {
  numberOfLeafs: number
}

export type AlgorithmParamsKeys =
  | keyof HorizonsExtensionParams
  | keyof FaultsSeparationParams
  | keyof SeismophationsParams
  | keyof StickSeparationParams

export type AlgorithmParams =
  | HorizonsExtensionParams
  | FaultsSeparationParams
  | SeismophationsParams
  | StickSeparationParams

export type ObjectTypes = typeof SEISMIC_SURVEY | typeof HORIZON | typeof POLYGON
type Parameters = 'amplitude' | 'twt' | 'z' | 'frequency' | 'shift'
export type BaseEntity<IdKey extends BaseObjectTypes = BaseObjectTypes> = Pick<
  SelectedTreeObject<IdKey>,
  'entityId' | 'entityType' | 'entityName'
>

type BaseObject = {
  type: Nullable<ObjectTypes>
  entity: Nullable<BaseEntity>
}
type Parameter = {
  parameter: Nullable<Parameters>
}

export type BaseObjectProperties = BaseObject & Parameter

type SeismicSurveyObject = BaseObject & Parameter
type HorizonObject = BaseObject & Parameter
type PolygonObject = BaseObject

export type HorizonCalculation = {
  seismicSurvey: SeismicSurveyObject
  horizon: HorizonObject
}
export type FaultsCalculation = {
  seismicSurvey: SeismicSurveyObject
}
export type SeismophationsCalculation = {
  seismicSurvey: SeismicSurveyObject
  horizon: HorizonObject
  polygon: PolygonObject
}

export type StickCalculation = {
  seismicSurvey: SeismicSurveyObject
}

type CalculationParams =
  | HorizonCalculation
  | FaultsCalculation
  | SeismophationsCalculation
  | StickCalculation

export type Calculation = {
  id: number
  name: string
  params: Nullable<CalculationParams>
}

export type FormDataML = {
  type: AlgorithmTypes
  algorithm: AlgorithmParams
  calculations: Calculation[]
}

export type MLServiceToUseType = 'IAAS' | 'PAAS'
